import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { useTheme } from "@/context/ThemeContext";
import { ShoppingBag, User, LogOut, Menu, Search, ShieldCheck, Heart, Sun, Moon } from "lucide-react";
import { useState } from "react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { logo } = useSiteSettings();
  const theme = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 bg-[#F4F4F0] border-b-[3px] border-black" data-testid="navbar">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          {logo && (
            <img src={logo} alt="OUTLAW-D" className="h-10 w-10 sm:h-12 sm:w-12 rounded-full border-2 border-black object-cover" data-testid="nav-logo-img" />
          )}
          <span className="font-display text-2xl sm:text-3xl leading-none hidden sm:inline whitespace-nowrap">OUTLAW-D</span>
          <span className="sticker pink hidden md:inline-block">.in</span>
        </Link>
        <nav className="hidden md:flex items-center gap-6 font-mono-b text-sm uppercase font-bold">
          <Link to="/shop" data-testid="nav-shop" className="hover:underline underline-offset-4">Shop</Link>
          <Link to="/shop/jackets" data-testid="nav-jackets" className="hover:underline underline-offset-4">Jackets</Link>
          <Link to="/shop/tees" data-testid="nav-tees" className="hover:underline underline-offset-4">Tees</Link>
          <Link to="/shop/jeans" data-testid="nav-jeans" className="hover:underline underline-offset-4">Jeans</Link>
          <Link to="/shop/thrifted" data-testid="nav-thrifted" className="hover:underline underline-offset-4">Thrifted</Link>
          <Link to="/sell" data-testid="nav-sell" className="bg-[#D4FF00] px-3 py-1 border-2 border-black brutal-shadow-sm hover-nudge">Sell your fits</Link>
        </nav>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const q = new FormData(e.currentTarget).get("q");
            if (q) navigate(`/shop?q=${encodeURIComponent(q)}`);
          }}
          className="hidden lg:flex items-center relative"
          data-testid="nav-search-form"
          role="search"
        >
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-black/60" />
          <input
            type="search"
            name="q"
            data-testid="nav-search-input"
            placeholder="Search the streets..."
            className="brutal-input pl-9 py-2 w-52 text-xs uppercase"
          />
        </form>
        <div className="flex items-center gap-3">
          <button data-testid="nav-search" onClick={() => navigate("/shop")} className="hidden sm:block"><Search className="w-5 h-5" /></button>
          <button
            data-testid="nav-theme-toggle"
            onClick={() => theme?.toggle?.()}
            aria-label="Toggle dark mode"
            className="hidden sm:flex items-center justify-center w-8 h-8 border-2 border-black bg-white hover-nudge"
          >
            {theme?.dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <Link to="/cart" data-testid="nav-cart" className="relative">
            <ShoppingBag className="w-6 h-6" />
            {count > 0 && (
              <span className="absolute -top-2 -right-2 bg-[#FF007F] text-white text-[10px] w-5 h-5 flex items-center justify-center border-2 border-black rounded-full font-bold">{count}</span>
            )}
          </Link>
          {user && (
            <Link to="/account?tab=wishlist" data-testid="nav-wishlist" className="hidden sm:block"><Heart className="w-5 h-5" /></Link>
          )}
          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="border-2 border-black px-2 py-1 bg-[#D4FF00] text-xs font-bold hover-nudge"><ShieldCheck className="inline w-4 h-4 mr-1" />ADMIN</Link>
              )}
              <Link to="/account" data-testid="nav-account" className="border-2 border-black px-2 py-1 text-xs font-bold hover-nudge"><User className="inline w-4 h-4 mr-1" />{user.name.split(" ")[0].toUpperCase()}</Link>
              <button onClick={logout} data-testid="nav-logout" className="border-2 border-black px-2 py-1 text-xs font-bold hover-nudge bg-white"><LogOut className="w-4 h-4" /></button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login" className="hidden sm:block border-2 border-black px-3 py-1 text-xs font-bold bg-black text-white hover-nudge">LOGIN</Link>
          )}
          <button data-testid="nav-menu-toggle" className="md:hidden" onClick={() => setOpen(!open)}><Menu /></button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t-2 border-black px-6 py-4 bg-[#F4F4F0] font-mono-b uppercase font-bold text-sm space-y-3" data-testid="mobile-menu">
          <Link to="/shop" onClick={() => setOpen(false)} className="block">All</Link>
          <Link to="/shop/jackets" onClick={() => setOpen(false)} className="block">Jackets</Link>
          <Link to="/shop/tees" onClick={() => setOpen(false)} className="block">Tees</Link>
          <Link to="/shop/jeans" onClick={() => setOpen(false)} className="block">Jeans</Link>
          <Link to="/shop/tote-bags" onClick={() => setOpen(false)} className="block">Totes</Link>
          <Link to="/shop/thrifted" onClick={() => setOpen(false)} className="block">Thrifted</Link>
          <Link to="/sell" onClick={() => setOpen(false)} className="block bg-[#D4FF00] border-2 border-black px-3 py-2">Sell your fits</Link>
          {user ? (
            <>
              <Link to="/account" onClick={() => setOpen(false)} className="block">My Account</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="block">Admin</Link>}
              <button onClick={() => { logout(); setOpen(false); }} className="block">Logout</button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="block">Login</Link>
          )}
        </div>
      )}
    </header>
  );
}
